import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import { load, start, resetCompositionState } from '../../store/composition/composition.js';
import { compositionTypes } from '../../store/composition/helpers.js';
import { launchElearning } from '../../store/elearning/elearning.js';
import { translate } from '../../helpers/translate.js';
import '@sdb-develop/learning-frontend';
import '../shared/sdb-player-dialog.js';

@customElement('sdb-composition-split-button')
class SdbCompositionSplitButton extends connect(store)(LitElement)
 {
    @property({ type: String })
    accessor compositionId;

    @property({type: String})
    accessor catalogName

    @property({type: Boolean})
    accessor startDisabled = false;

    @property({type: Boolean})
    accessor resetDisabled = false;

    @property({type: Boolean})
    accessor light = false;

    @state()
    accessor composition;

    @state()
    accessor isLoading = false;

    @state()
    accessor ready = false;

    @query('#sdb_composition_reset_dialog')
    accessor resetAllDialog;

    get caption() {
        switch (this.composition.progress) {
            case 'incomplete': return translate('btnIncomplete');
            case 'completed': return translate('btnCompleted2');
            default: return translate('btnStart');
        }
    }

    get isSingleItem() {
        return this.composition.compositionType === compositionTypes.COMPOSITIONTYPE_SINGLEITEM
            && this.composition.items.length > 0;
    }

    stateChanged(state) {
        this.composition = state.composition.data;
        this.isLoading = state.composition.status === 'loading';
        this.ready = !this.isLoading && !!state.composition.data;
        this.noProgress = !state.composition.data || !state.composition.data.progress || state.composition.data.progress === 'started';
    }

    static get styles() {
        return css`
            sdb-content-split-button[light] {
                --text-light: var(--tcg-primary-color-ontertiary, #fff);
            }
        `;
    }

    render() {
        if (!this.ready)
            return nothing;

        if (this.noProgress) {
            return html`
                <sdb-content-button
                    label=${this.caption}
                    @click=${(e) => { this._executeStart(e); }}>
                </sdb-content-button>
            `;
        }

        return html`
            ${this.composition.progress !== 'completed' ? html`
                <sdb-content-split-button
                    primaryLabel=${this.caption}
                    paddingSplitButton="10px"
                    menuDismissible
                    ?disabled=${this.isLoading || this.startDisabled}
                    .primaryAction=${(e) => { this._executeAction(e); }}
                    .actions=${[
                        {
                            label: translate('btnReset'),
                            action: (e) => { this.resetAllDialog.open(); },
                            disabled: this.resetDisabled
                        },
                    ]}>
                </sdb-content-split-button>`: html`
                <sdb-content-split-button
                    primaryLabel=${translate('btnReset')}
                    paddingSplitButton="10px"
                    menuDismissible
                    ?disabled=${this.isLoading || this.resetDisabled}
                    .primaryAction=${(e) => { this.resetAllDialog.open(); }}
                    .actions=${[
                        {
                            label: this.caption,
                            action: (e) => { this._executeAction(e); },
                            disabled: this.startDisabled,
                        },
                    ]}>
                </sdb-content-split-button>
            `}

            <sdb-player-dialog
                id="sdb_composition_reset_dialog"
                type="confirm"
                heading=${translate('dlgResetHeader')}
                body=${translate(this.isSingleItem ? 'dlgResetBodySingleItem' : 'dlgResetBody', {
                    catalogName:  (this.catalogName) ? this.catalogName : this.composition.title
                })}
                confirmCaption=${translate('btnReset')}
                @confirm=${(e) => { this._executeReset(e); }}>
            </sdb-player-dialog>
        `;
    }

    firstUpdated() {
        if (this.compositionId) {
            store.dispatch(load({ compositionId: this.compositionId }));
        }
    }

    _executeStart(e) {
        store.dispatch(start({ compositionId: this.composition.id }));
        this._executeAction(e);
    }

    _executeAction(e) {
        if (this.isSingleItem) {
            store.dispatch(launchElearning({ item: this.composition.items[0] }));
        }

        this.dispatchEvent(
            new CustomEvent('start', {
                bubbles: true,
                composed: true,
                detail: {
                    isSingleItem: this.isSingleItem,
                },
                target: e.target
            })
        );
    }

    _executeReset(e) {
        store.dispatch(resetCompositionState({ compositionId: this.composition.id }));

        this.dispatchEvent(
            new CustomEvent('reset', {
                bubbles: true,
                composed: true,
                detail: {
                    isSingleItem: this.isSingleItem,
                },
                target: e.target
            })
        );
    }

    _openResetDialog() {
        // our dialog can now be found in the regular dom
        document.getElementById('sdb-composition-reset-dialog').opened = true;
    }
}
